import type {
  AllowedDay,
  InterventionGroupTemplateResponse,
  InterventionTemplateResponse,
  PlacementRule
} from '../../../api/generated';
import { EDITOR_DAYS } from './constants';
import type {
  EditorAllowedDay,
  EditorDayKey,
  EditorDependency,
  EditorNode,
  EditorState
} from './types';

const EDITOR_DAY_SET = new Set<string>(EDITOR_DAYS);

function isEditorDay(day: AllowedDay['day']): day is EditorDayKey {
  return !!day && EDITOR_DAY_SET.has(day);
}

// Drops entries without a `day`; otherwise passes the API shape through
// (timeWindows are already the API type, so no field-level mapping).
function apiAllowedDaysToEditor(
  allowedDays: AllowedDay[] | undefined
): EditorAllowedDay[] | undefined {
  if (!allowedDays) return undefined;
  const mapped: EditorAllowedDay[] = [];
  for (const d of allowedDays) {
    if (!isEditorDay(d.day)) continue;
    mapped.push({ day: d.day, timeWindows: d.timeWindows ?? [] });
  }
  return mapped;
}

export function groupToEditor(group: InterventionGroupTemplateResponse): EditorState {
  const nodes: EditorNode[] = group.interventions.map((iv) => ({
    id: iv.id,
    intervention: iv,
    dependencies: [],
    allowedDays: apiAllowedDaysToEditor(iv.allowedDays)
  }));
  for (const rule of group.placementRules) {
    if (!rule.sourceInterventionId || !rule.type) continue;
    const node = nodes.find((n) => n.id === rule.sourceInterventionId);
    if (!node) continue;
    node.dependencies.push({
      id: crypto.randomUUID(),
      type: rule.type,
      targetId: rule.targetInterventionId
    });
  }
  return nodes;
}

export function editorToGroup(
  prev: InterventionGroupTemplateResponse,
  editor: EditorState
): InterventionGroupTemplateResponse {
  const interventions: InterventionTemplateResponse[] = editor.map((node) => ({
    ...node.intervention,
    id: node.id,
    allowedDays: node.allowedDays
  }));
  const placementRules: PlacementRule[] = editor.flatMap((node) =>
    node.dependencies.map((dep) => ({
      sourceInterventionId: node.id,
      targetInterventionId: dep.targetId,
      type: dep.type
    }))
  );
  return { ...prev, interventions, placementRules };
}

export function makeEditorNodeFromCatalog(template: InterventionTemplateResponse): EditorNode {
  // Mint a fresh client-side id so duplicates of the same catalog item stay
  // distinct when referenced by placement rules.
  const id = crypto.randomUUID();
  return {
    id,
    intervention: { ...template, id },
    dependencies: [],
    allowedDays: apiAllowedDaysToEditor(template.allowedDays)
  };
}

export function setNodeAllowedDays(
  node: EditorNode,
  allowedDays: EditorAllowedDay[] | undefined
): EditorNode {
  return { ...node, allowedDays };
}

export function addDependency(node: EditorNode, dep: Omit<EditorDependency, 'id'>): EditorNode {
  return { ...node, dependencies: [...node.dependencies, { id: crypto.randomUUID(), ...dep }] };
}
